<template>
  <div id="app" :class="{ 'app--impersonating': impersonatedBy }">
    <!-- ── Banner de impersonação (suporte) ── -->
    <div v-if="impersonatedBy" class="imp-banner">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a5 5 0 0 0-5 5v2a5 5 0 0 0 10 0V7a5 5 0 0 0-5-5z"/><path d="M4 22a8 8 0 0 1 16 0"/></svg>
      <span>Você está no sistema desta obra como <strong>administrador</strong> (suporte, iniciado por {{ impersonatedBy }}).</span>
      <button class="imp-exit" type="button" @click="exitImpersonation">Sair e voltar ao painel</button>
    </div>

    <router-view />
    <AppDialogs />

    <!-- ── Offline timeout block screen ── -->
    <div v-if="offlineBlocked" class="offline-block-overlay">
      <div class="offline-block-card">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="22" stroke="#d83b01" stroke-width="2.5"/>
          <path d="M14 14l20 20M34 14L14 34" stroke="#d83b01" stroke-width="2.5" stroke-linecap="round"/>
        </svg>
        <h2>Acesso offline expirado</h2>
        <p>Você ficou offline por mais de {{ daysOffline }} dias. Conecte-se à internet para continuar usando o sistema.</p>
        <div class="offline-block-status">
          <span v-if="isOffline" class="offline-badge">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><circle cx="5" cy="5" r="4" fill="#d83b01"/></svg>
            Sem conexão
          </span>
          <span v-else class="online-badge">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><circle cx="5" cy="5" r="4" fill="#107c10"/></svg>
            Conectado — sincronizando…
          </span>
        </div>
      </div>
    </div>

    <!-- ── Pending sync banner ── -->
    <transition name="sync-banner">
      <div v-if="offlinePendingCount > 0 && !isOffline && !offlineSyncing" class="sync-banner" @click="triggerSync">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12 7A5 5 0 112 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M12 4v3h-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        {{ offlinePendingCount }} relatório(s) aguardando envio — clique para sincronizar
      </div>
    </transition>
    <div v-if="offlineSyncing" class="sync-banner sync-banner--active">
      <svg class="spin" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M12 7A5 5 0 112 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      Sincronizando relatórios…
    </div>

    <!-- ── Auto-update: toast sutil enquanto aplica a nova versão ── -->
    <transition name="update-banner">
      <div v-if="applyingUpdate" class="update-banner update-banner--auto">
        <svg class="spin" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
        <span>Atualizando para a nova versão…</span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { themeStore } from '@/store/theme'
import { sessionStore } from '@/store/session'
import AppDialogs from '@/components/AppDialogs.vue'
import {
  sendHeartbeat, syncPendingReports, checkOfflineTimeout,
  offlineBlocked, offlinePendingCount, offlineSyncing,
  isOffline, daysOffline, isOfflineModeEnabled
} from '@/composables/useOfflineMode'
import { swUpdateReady, applyPendingUpdateSafely } from '@/store/swUpdateStore'
import '@/store/installPromptStore' // inicializa captura do beforeinstallprompt

export default defineComponent({
  name: 'App',
  components: { AppDialogs },
  setup() {
    const router = useRouter()

    // ── Impersonação: banner + sair ──
    const impersonatedBy = ref('')
    const refreshImpersonation = () => {
      try {
        impersonatedBy.value = sessionStore.state.user ? (window.sessionStorage.getItem('imp_by') || '') : ''
      } catch { impersonatedBy.value = '' }
    }
    const exitImpersonation = () => {
      try { window.sessionStorage.removeItem('imp_by') } catch { /* ignore */ }
      sessionStore.logout()
      impersonatedBy.value = ''
      window.location.href = '/administrador'
    }

    const triggerSync = () => syncPendingReports()

    // ── Auto-update: aplica a nova versão sozinho, sem interromper o usuário ──
    // Aplica na próxima navegação de rota (nunca no meio de uma tela/formulário)
    // ou quando a aba volta a ficar visível.
    const applyingUpdate = ref(false)
    const tryAutoUpdate = () => {
      if (!swUpdateReady.value || applyingUpdate.value) return
      applyingUpdate.value = true
      // Pequeno atraso para o toast aparecer antes do reload
      setTimeout(() => { applyPendingUpdateSafely() }, 700)
    }
    const onVisibility = () => { if (document.visibilityState === 'visible') tryAutoUpdate() }

    const handleUnauthorized = () => {
      const systemUsername = sessionStore.state.user?.systemUsername
      sessionStore.logout()
      router.replace(systemUsername ? `/login/${systemUsername}` : '/')
    }

    // Aplica atualização pendente ao trocar de rota (momento seguro)
    router.afterEach(() => { tryAutoUpdate() })

    onMounted(async () => {
      themeStore.init()
      refreshImpersonation()
      window.addEventListener('auth:unauthorized', handleUnauthorized)
      document.addEventListener('visibilitychange', onVisibility)

      if (isOfflineModeEnabled()) {
        checkOfflineTimeout()

        if (!isOffline.value) {
          await sendHeartbeat()
          await syncPendingReports()
        }
      }
    })

    onUnmounted(() => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
      document.removeEventListener('visibilitychange', onVisibility)
    })

    watch(isOffline, async (offline) => {
      if (!offline && isOfflineModeEnabled()) {
        await sendHeartbeat()
        await syncPendingReports()
        checkOfflineTimeout()
      }
    })

    // Atualiza o banner ao trocar de rota (entrar/sair da impersonação)
    watch(() => router.currentRoute.value.fullPath, () => refreshImpersonation())

    return {
      offlineBlocked, offlinePendingCount, offlineSyncing,
      isOffline, daysOffline, triggerSync,
      applyingUpdate,
      impersonatedBy, exitImpersonation,
    }
  }
})
</script>

<style lang="scss">
/* ── Banner de impersonação (suporte) ── */
.imp-banner {
  position: fixed; top: 0; left: 0; right: 0; z-index: 9999;
  display: flex; align-items: center; gap: 10px;
  padding: 7px 16px;
  background: repeating-linear-gradient(45deg, #b45309, #b45309 12px, #a24708 12px, #a24708 24px);
  color: #fff; font-size: 12.5px; font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,.25);
  span { flex: 1; min-width: 0; }
  strong { font-weight: 800; }
}
.imp-exit {
  flex-shrink: 0; padding: 5px 14px; border-radius: 7px; cursor: pointer;
  background: #fff; color: #b45309; border: none; font-size: 12px; font-weight: 800;
  &:hover { background: #fff7ed; }
}
.app--impersonating { padding-top: 34px; }

/* ─────────────────────────────────────────────
   DESIGN TOKENS — M365-inspired
───────────────────────────────────────────── */
:root {
  /* Brand — overridden per obra via applySystemTheme() */
  --system-navbar: #0f4c81;
  --system-navbar-text: #ffffff;
  --system-sidebar: #0a3460;
  --system-sidebar-text: rgba(255, 255, 255, 0.85);
  --system-sidebar-muted: rgba(255, 255, 255, 0.5);
  --system-sidebar-active: rgba(255, 255, 255, 0.12);
  --system-sidebar-hover: rgba(255, 255, 255, 0.07);
  --system-search-bg: rgba(255, 255, 255, 0.14);
  --system-search-border: rgba(255, 255, 255, 0.22);

  /* Text */
  --ink: #201f1e;
  --ink-muted: #605e5c;
  --ink-faint: #a19f9d;

  /* Accent (links, active elements, badges) */
  --accent: #0078d4;
  --accent-hover: #005ea2;
  --accent-light: #deecf9;
  --accent-2: #107c10;
  --danger: #a4262c;
  --warning: #d83b01;

  /* Surfaces */
  --bg: #f3f2f1;
  --surface: #ffffff;
  --surface-2: #f8f7f6;
  --surface-3: #edebe9;
  --card: #ffffff;

  /* Borders */
  --border: rgba(0, 0, 0, 0.1);
  --border-strong: rgba(0, 0, 0, 0.2);

  /* Shadows */
  --shadow-sm: 0 1.5px 4px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 16px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.08);
  --card-shadow: var(--shadow-md);

  /* Radius */
  --radius-sm: 3px;
  --radius: 6px;
  --radius-lg: 10px;
  --radius-xl: 14px;

  /* Font */
  --font-body: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-title: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;

  /* Navbar height */
  --navbar-h: 48px;
}

/* ─────────────────────────────────────────────
   WINDOW CONTROLS OVERLAY (Desktop PWA)
───────────────────────────────────────────── */
@media (display-mode: window-controls-overlay) {
  :root {
    --navbar-h: env(titlebar-area-height, 48px);
    --wco-x: env(titlebar-area-x, 0px);
    --wco-width: env(titlebar-area-width, 100%);
  }

  .home-nav {
    padding-left: max(env(titlebar-area-x, 0px), 12px);
    padding-right: calc(100% - env(titlebar-area-x, 0px) - env(titlebar-area-width, 100%) + 12px);
    height: env(titlebar-area-height, 48px);
    -webkit-app-region: drag;
    app-region: drag;
  }

  /* Interactive elements inside the navbar must NOT be draggable */
  .home-nav button,
  .home-nav a,
  .home-nav input,
  .home-nav .user-menu,
  .home-nav .compact-search-wrap {
    -webkit-app-region: no-drag;
    app-region: no-drag;
  }
}

/* ─────────────────────────────────────────────
   DARK MODE
───────────────────────────────────────────── */
[data-theme="dark"] {
  --ink: #f3f2f1;
  --ink-muted: #c8c6c4;
  --ink-faint: #706e6b;

  --accent: #4ba0e4;
  --accent-hover: #6ab3ec;
  --accent-light: rgba(75, 160, 228, 0.15);

  --bg: #1b1a19;
  --surface: #252423;
  --surface-2: #2d2b29;
  --surface-3: #323130;
  --card: #252423;

  --border: rgba(255, 255, 255, 0.09);
  --border-strong: rgba(255, 255, 255, 0.18);

  --shadow-sm: 0 1.5px 4px rgba(0, 0, 0, 0.35);
  --shadow-md: 0 4px 16px rgba(0, 0, 0, 0.45);
  --shadow-lg: 0 8px 32px rgba(0, 0, 0, 0.6);
}

/* ─────────────────────────────────────────────
   RESET & BASE
───────────────────────────────────────────── */
* {
  box-sizing: border-box;
}

body {
  margin: 0;
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.5;
  color: var(--ink);
  background: var(--bg);
  min-height: 100vh;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  min-height: 100vh;
  overflow-x: hidden;
}

a {
  color: inherit;
  text-decoration: none;
}

button,
input,
select,
textarea {
  font-family: inherit;
  font-size: inherit;
}

/* ─────────────────────────────────────────────
   LAYOUT UTILITIES
───────────────────────────────────────────── */
.page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 20px 72px;
  background: var(--bg);
}

.grid {
  display: grid;
  gap: 16px;
}

.card-list {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

/* ─────────────────────────────────────────────
   CARD
───────────────────────────────────────────── */
.card {
  background: var(--card);
  border-radius: var(--radius-lg);
  box-shadow: var(--card-shadow);
  border: 1px solid var(--border);
  padding: 28px 32px;
  width: min(960px, 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.item-card {
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  padding: 16px 18px;
  display: grid;
  gap: 10px;
  background: var(--card);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.item-card:hover {
  box-shadow: var(--shadow-md);
  border-color: var(--border-strong);
}

.item-title {
  font-family: var(--font-title);
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: var(--ink);
}

.item-meta {
  color: var(--ink-muted);
  font-size: 12px;
}

/* ─────────────────────────────────────────────
   TYPOGRAPHY
───────────────────────────────────────────── */
.title {
  font-family: var(--font-title);
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: var(--ink);
}

.subtitle {
  margin: 4px 0 0;
  color: var(--ink-muted);
  font-size: 13px;
}

.section {
  border-top: 1px solid var(--border);
  padding-top: 18px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  font-family: var(--font-title);
  color: var(--ink);
}

.hint {
  color: var(--ink-muted);
  font-size: 13px;
}

/* ─────────────────────────────────────────────
   BUTTONS
───────────────────────────────────────────── */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid transparent;
  border-radius: var(--radius);
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
  white-space: nowrap;
  line-height: 1.4;
}

.btn:active {
  transform: translateY(1px);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-primary {
  background: var(--system-navbar, #0f4c81);
  color: var(--system-navbar-text, #ffffff);
  border-color: transparent;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.1);
}

.btn-secondary {
  background: var(--surface-3);
  color: var(--ink);
  border-color: var(--border);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--surface-2);
  border-color: var(--border-strong);
}

.btn-accent {
  background: var(--accent);
  color: #fff;
  border-color: transparent;
}

.btn-accent:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-ghost {
  background: transparent;
  color: var(--ink);
  border-color: var(--border-strong);
}

.btn-ghost:hover:not(:disabled) {
  background: var(--surface-3);
}

.btn-danger {
  background: var(--danger);
  color: #fff;
  border-color: transparent;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  border: 1px solid transparent;
  background: transparent;
  color: var(--ink);
  font-size: 18px;
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.12s ease;
}

.icon-btn:hover {
  background: var(--surface-3);
}

/* ─────────────────────────────────────────────
   FORMS
───────────────────────────────────────────── */
.form-grid {
  display: grid;
  gap: 16px;
}

.input-field {
  display: grid;
  gap: 5px;
}

.input-field label {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-muted);
}

.input-field input,
.input-field select,
.input-field textarea {
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 8px 10px;
  font-size: 14px;
  background: var(--surface);
  color: var(--ink);
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.input-field input:focus,
.input-field select:focus,
.input-field textarea:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-light);
}

/* ─────────────────────────────────────────────
   TABS
───────────────────────────────────────────── */
.tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0;
}

.tab {
  padding: 8px 14px;
  border-radius: var(--radius) var(--radius) 0 0;
  border: 1px solid transparent;
  border-bottom: none;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  background: transparent;
  color: var(--ink-muted);
  margin-bottom: -1px;
  transition: color 0.12s ease, background 0.12s ease;
}

.tab:hover {
  color: var(--ink);
  background: var(--surface-3);
}

.tab.active {
  background: var(--surface);
  color: var(--system-navbar, #0f4c81);
  border-color: var(--border);
  border-bottom-color: var(--surface);
  font-weight: 600;
}

/* ─────────────────────────────────────────────
   PILLS / BADGES
───────────────────────────────────────────── */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: var(--accent-light);
  color: var(--accent);
}

/* ─────────────────────────────────────────────
   LIST HELPERS
───────────────────────────────────────────── */
.list-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}

/* ─────────────────────────────────────────────
   IMAGE HELPERS
───────────────────────────────────────────── */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 10px;
}

.image-preview,
.image-add {
  width: 80px;
  height: 80px;
  border-radius: var(--radius-lg);
  border: 1px dashed var(--border-strong);
  display: grid;
  place-items: center;
  overflow: hidden;
  background: var(--surface-3);
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-add {
  font-size: 24px;
  color: var(--accent);
  background: var(--accent-light);
  cursor: pointer;
  transition: background 0.12s ease;
}

.image-add:hover {
  background: var(--accent-light);
  filter: brightness(0.95);
}

.hidden-input {
  display: none;
}

/* ─────────────────────────────────────────────
   RESPONSIVE
───────────────────────────────────────────── */
@media (max-width: 720px) {
  .card {
    padding: 20px 16px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* ─────────────────────────────────────────────
   OFFLINE BLOCK OVERLAY
───────────────────────────────────────────── */
.offline-block-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.85);
  display: grid;
  place-items: center;
  z-index: 9999;
  backdrop-filter: blur(6px);
}

.offline-block-card {
  background: var(--card);
  border-radius: 20px;
  padding: 48px 40px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: var(--ink);
  }

  p {
    margin: 0;
    font-size: 14px;
    color: var(--ink-muted);
    line-height: 1.6;
  }
}

.offline-block-status {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  background: var(--surface-3);
}

.offline-badge  { color: #d83b01; display: flex; align-items: center; gap: 5px; }
.online-badge   { color: #107c10; display: flex; align-items: center; gap: 5px; }

/* ─────────────────────────────────────────────
   SYNC BANNER
───────────────────────────────────────────── */
.sync-banner {
  position: fixed;
  bottom: 72px;
  right: 20px;
  z-index: 900;
  background: #0078d4;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 24px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.18);
  display: flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }
  &--active { cursor: default; background: #005ea2; }
}

.sync-banner-enter-active, .sync-banner-leave-active { transition: opacity 0.2s, transform 0.2s; }
.sync-banner-enter-from, .sync-banner-leave-to { opacity: 0; transform: translateY(8px); }

.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ─────────────────────────────────────────────
   SW UPDATE BANNER
───────────────────────────────────────────── */
.update-banner {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #1e293b;
  color: #f8fafc;
  font-size: 13px;
  font-weight: 500;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.28);
  white-space: nowrap;
}

.update-banner-btn {
  margin-left: 4px;
  padding: 5px 14px;
  background: #38bdf8;
  color: #0c1a2e;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s;
  &:hover { background: #7dd3fc; }
}

.update-banner-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(255,255,255,.1);
  color: #f8fafc;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
  &:hover { background: rgba(255,255,255,.2); }
}

.update-banner-enter-active, .update-banner-leave-active { transition: opacity .25s, transform .25s; }
.update-banner-enter-from, .update-banner-leave-to { opacity: 0; transform: translateX(-50%) translateY(12px); }
</style>
